import DateJS, { format2DateStr, format2TimeStamp } from '@/utils/date';
import * as EditService from '@/api/orderEdit';
import { EditRentRoomInfo } from '@/redux/reducers/reservation/base';
import { ReducerState, MainState } from '@/redux/reducers/reservation/checkInEditor';
import { createTargetGetter, TargetType } from '@/components/Reservation/PopupTarget';
import getRandomId from '@/utils/uuid';
import { parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import { findItemByKey, flatPriceList } from '@/redux/actions/reservation/helper';

const formatBaseData: (data) => MainState = (data) => {
  const {
    occType,
    occDuration,
    expectedDepartureTime,

    sourceChannelId,
    sourceChannelName,
    priceStrategyId,
    priceStrategyName,
    channelOrderNo,
    priceChannel,

    // memberFlag,
    memberLevel,
    remark,
    checkinRemark,
    wyuid,
    resvNo,
    subOrderCreateVOS,
  } = data;
  // 入住日期id索引
  const rentDateIdMap = {};
  // 原始券列表
  const originCouponList = [];
  // 原始门市价
  const originMarketPriceMap = {};
  // 原始房价 房间id-日期-房价
  const originRoomPriceMap: Record<string, Record<string, string>> = {};

  const rentRoomInfos = (subOrderCreateVOS || []).map((roomItem) => {
    const { recNo } = roomItem;
    const roomPriceMap = {};
    originRoomPriceMap[roomItem.roomId] = roomPriceMap;
    return <EditRentRoomInfo>{
      indexId: getRandomId(),
      roomTypeId: roomItem.roomTypeId,
      roomTypeName: roomItem.roomTypeName,
      roomId: roomItem.roomId,
      roomNo: roomItem.roomNo,
      recNo,
      deposit: parseFen2Yuan(roomItem.deposit),
      roomPriceList: (roomItem.subOrderRoomPriceCreateVOS || []).map((rentDay) => {
        const {
          rentDate,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          price,
          billFlag,
          marketStrategyPrice,
        } = rentDay;

        const rentDateText = format2DateStr(rentDate);
        const inputPrice = parseFen2Yuan(price);
        rentDateIdMap[rentDate] = rentDateIdMap[rentDate] || getRandomId();
        originMarketPriceMap[`${recNo}|${rentDateText}`] = marketStrategyPrice;
        roomPriceMap[rentDateText] = inputPrice;

        if (!billFlag) {
          // 没过夜审
          originCouponList.push(couponNo);
        }

        return {
          rentDateId: rentDateIdMap[rentDate],
          occType,
          rentDate: rentDateText,
          inputPrice,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          couponValue: parseFen2Yuan(couponAmount),
          isBeforeAudit: !!billFlag, // 是否过夜审【是否抛账】
          priceChannel,
        };
      }),
    };
  });

  return {
    originBaseInfo: {
      renewalId: null,
      isLatestRecord: false,
      resvNo,
      expectedArrivalTime: null,
      occDuration,
      expectedDepartureTime,
      sourceChannelId,
      sourceChannelName,
      priceChannel,
      priceStrategyId,
      priceStrategyName,
      channelOrderNo,
      remark,
      checkInRemark: checkinRemark,
    },
    originCouponList,
    originMarketPriceMap,
    originRoomPriceMap,
    commonInfo: {
      memberLevel,
      ownerWYId: wyuid,
      // 默认场景 (直连渠道、已过夜审)
      isDirectChannel: true,
      auditPassedCount: 999,
    },
    rentTimeInfo: {
      occType,
      expectedArrivalTime: null,
      rentTime: occDuration,
      expectedDepartureTime: DateJS(expectedDepartureTime),
    },
    channelInfo: {
      channelId: sourceChannelId,
      strategyId: priceStrategyId,
      channelOrderNo,
    },
    rentRoomInfos,
    remarkInfo: {
      remark,
      checkInRemark: checkinRemark,
    },
  };
};

export interface RenewalRecord {
  id: number; // 续住记录id
  checked: boolean; // 是否选中(当前编辑的目标)
}
// 获取续住状态
const getRenewalState: (list: RenewalRecord[]) => Promise<MainState> = async (list) => {
  const targetIndex = list.findIndex((item) => item.checked);
  const { id } = list[targetIndex];
  const res = await EditService.getRenewalOrderDetail({ id });
  const { expectedArrivalTime } = res.data;
  const baseState = formatBaseData(res.data);
  baseState.originBaseInfo.expectedArrivalTime = expectedArrivalTime;
  baseState.rentTimeInfo.businessDate = DateJS(expectedArrivalTime)
    .subtract(1, 'day')
    .startOf('day'); // 默认预抵时间前一天
  baseState.rentTimeInfo.expectedArrivalTime = DateJS(expectedArrivalTime);
  baseState.originBaseInfo.renewalId = id;
  // 是否是最新的一条续住记录
  baseState.originBaseInfo.isLatestRecord = targetIndex + 1 === list.length;
  return baseState;
};

// 获取入住单状态
const getCheckInState: (recNos: string[]) => Promise<MainState> = async (recNos) => {
  const res = await EditService.getCheckInOrderDetail({ recNos });
  const { checkinTime } = res.data;
  const baseState = formatBaseData(res.data);
  baseState.originBaseInfo.expectedArrivalTime = checkinTime;
  baseState.rentTimeInfo.businessDate = DateJS(checkinTime).subtract(1, 'day').startOf('day'); // 默认入住时间前一天
  baseState.rentTimeInfo.expectedArrivalTime = DateJS(checkinTime);
  return baseState;
};

export const getStateFromOrder: (config: {
  renewalList?: RenewalRecord[];
  recList?: string[];
}) => Promise<MainState> = async (config) => {
  const { renewalList, recList } = config;
  if (renewalList) {
    // 修改续住
    return getRenewalState(renewalList);
  }
  if (recList && recList.length) {
    return getCheckInState(recList);
  }
  return Promise.reject();
};

const formatBaseSubmitData = (data: ReducerState) => {
  const {
    originBaseInfo,
    commonInfo,
    rentTimeInfo,
    channelInfo,
    rentRoomInfos,
    remarkInfo,
    channelList,
    strategyList,
    roomPriceList,
    originMarketPriceMap,
  } = data;
  const { occType, rentTime, expectedDepartureTime } = rentTimeInfo;
  const { channelId, strategyId, channelOrderNo } = channelInfo;
  const sourceChannelName = findItemByKey(channelList, 'id', channelId)?.name;
  const priceStrategyName = findItemByKey(strategyList, 'id', strategyId)?.showName;
  const { cachePriceMap } = flatPriceList(roomPriceList);

  const subOrderCreateVOS = rentRoomInfos.map((room) => {
    const { roomId, roomNo, roomTypeId, roomTypeName, deposit, recNo } = room;
    const strategyPriceMap = cachePriceMap[roomTypeId] || {};
    return {
      recNo,
      roomId,
      roomNo,
      roomTypeId,
      roomTypeName,
      sourceChannelId: channelId,
      sourceChannelName,
      deposit: parseYuan2Fen(deposit),
      subOrderRoomPriceCreateVOS: (room.roomPriceList || []).map((dayInfo) => {
        const {
          rentDate,
          inputPrice,
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          couponSubType,
          crsCouponAmount,
        } = dayInfo;
        const dateTime = format2TimeStamp(rentDate);
        return {
          couponAmount,
          couponName,
          couponNo,
          couponType,
          couponWyuid,
          crsCouponAmount,
          couponSubType,
          price: parseYuan2Fen(inputPrice),
          // priceChannel: '',
          priceStrategyId: strategyId,
          rentDate: dateTime,
          strategyPrice: parseYuan2Fen(strategyPriceMap[rentDate]),
          marketStrategyPrice: originMarketPriceMap[`${recNo}|${rentDate}`],
        };
      }),
    };
  });

  return {
    wyuId: commonInfo.ownerWYId,
    resvNo: originBaseInfo.resvNo,
    channelOrderNo,
    channelOrderNoOld: originBaseInfo.channelOrderNo,
    expectedDepartureTime: format2TimeStamp(expectedDepartureTime),
    expectedDepartureTimeOld: originBaseInfo.expectedDepartureTime,
    occDuration: rentTime,
    occDurationOld: originBaseInfo.occDuration,
    occType,
    // priceChannel: '',
    priceChannelOld: originBaseInfo.priceChannel,
    priceStrategyId: strategyId,
    priceStrategyIdOld: originBaseInfo.priceStrategyId,
    priceStrategyName,
    priceStrategyNameOld: originBaseInfo.priceStrategyName,
    remark: remarkInfo.remark,
    remarkOld: originBaseInfo.remark,
    checkinRemark: remarkInfo.checkInRemark,
    checkinRemarkOld: originBaseInfo.checkInRemark,
    sourceChannelId: channelId,
    sourceChannelIdOld: originBaseInfo.sourceChannelId,
    sourceChannelName,
    sourceChannelNameOld: originBaseInfo.sourceChannelName,
    subOrderCreateVOS,
  };
};

// 续住修改数据
const formatRenewalSubmitData = (data: ReducerState) => {
  const { originBaseInfo } = data;
  const baseData = formatBaseSubmitData(data);
  return {
    ...baseData,
    extensionId: originBaseInfo.renewalId,
  };
};

// 入住修改数据
const formatCheckInSubmitData = (data: ReducerState) => {
  const baseData = formatBaseSubmitData(data);
  return baseData;
};

export const submitModifyData: (data: ReducerState) => Promise<void> = (data) => {
  const { originBaseInfo } = data;
  if (originBaseInfo.renewalId) {
    return EditService.modifyRenewalOrder(formatRenewalSubmitData(data));
  }
  if (originBaseInfo.resvNo) {
    return EditService.modifyCheckInOrder(formatCheckInSubmitData(data));
  }
  return Promise.reject();
};

// 定位节点
export const getPopupContainer = createTargetGetter(TargetType.CheckInEditor);
